import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useSettingsModal } from "../Context/SettingsContext";
import { setTitle } from "../helpers/MetaTag";
import { postData } from "../services/api";
import Button from "../components/form/Button";
import { Tooltip } from "react-tooltip";
import { useTranslation } from "react-i18next";
import NoDataFound from "../components/NoDataFound";
import Checkbox from "../components/form/Checkbox";
import QuestionItems from "../components/section/questionLibrary/QuestionItems";
import SelectedQuestionsItem from "../components/section/questionLibrary/SelectedQuestionsItem";
import { ManageDefaultConcernAndPredictorsSkleton } from "../components/loader/ManageDefaultConcernAndPredictorsSkleton";
import HeadingManagePopup from "../components/popup/HeadingManagePopup";
import ConfirmationPopup from "../components/popup/ConfirmationPopup";
import PositionWideQuestionLibrary from "../components/section/questionLibrary/PositionWideQuestionLibrary";
import Select from "../components/form/Select";
import { PositionTier } from "../constant/Constant";
import TierSelectDropdown from "../components/elements/TierSelectDropdown";
import toastr from "toastr";
import { sumTotal } from "../helpers";
import ExtendedTierPopup from "../components/popup/ExtendedTierPopup";
import { useNavigate } from "react-router-dom";

const QuestionsLibrary = () => {
  setTitle("Succession Planning Software for your Business with Succession Now | Questions Library");
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const { tierType, setTierType, isOpen, setIsOpen } = useSettingsModal();
  const user = useSelector((state) => state.auth?.user);
  const [selectedConcern, setSelectedConcern] = useState([]);
  const [selectedPredictors, setSelectedPredictors] = useState([]);
  const [concernLoading, setConcernLoading] = useState([]);
  const [predictorLoading, setPredictorLoading] = useState([]);
  const [concerns, setConcerns] = useState([]);
  const [predictors, setPredictors] = useState([]);
  const [actionValue, setActionValue] = useState({});
  const [enabledPopup, setEnabledPopup] = useState({ open: false, type: "" });
  const [editData, setEditData] = useState({});
  const [enabledConfirmationPopup, setEnabledConfirmationPopup] = useState(false);
  const [selectedTier, setSelectedTier] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [isUpdateConcern, setIsUpdateConcern] = useState(false);
  const [isUpdatePredictor, setIsUpdatePredictor] = useState(false);
  const [weightTotalValue, setWeightTotalValue] = useState(0);
  const [modalOpen, setModalOpen] = useState({
      open: false,
      data: null,
    });

  //Concern List Api Call
  const loadConcernList = useCallback(async () => {
    setConcernLoading(true);
    setIsUpdateConcern(false);
    try {
      const result = await postData("heading/list", {
        sortQuery: { created_at: 1 },
        status: 1,
        type: "concern",
        isGlobal: true,
        ...(tierType === PositionTier && { tierId: selectedTier }),
        tierType: tierType,
        limit: 9999,
      });

      if (result.data) {
        let _concerns = [];
        let globalConcern = [];
        result.data.map((item) => {
          globalConcern.push({
            ...item,
            isDefault: item?.company_headings.length > 0 ? true : false,
            disabled: false,
          });
          if (item?.company_headings.length > 0) {
            _concerns.push({
              _id: item?.company_headings[0]?._id,
              name: item?.company_headings[0]?.name,
              description: item?.company_headings[0]?.description,
              alias: item?.company_headings[0]?.alias,
              weight: item?.company_headings[0]?.weight,
              created_at: new Date(),
              isDefault: item?.isDefault ? item?.isDefault : false,
              disabled: false,
              tierId: item?.tierId,
            });
          }
        });
        setConcerns(globalConcern);
        setSelectedConcern(_concerns);
        setIsUpdateConcern(true);
      }
      setConcernLoading(false);
    } catch (err) {
      setConcernLoading(false);
      console.log(err.message);
    } finally {
      setConcernLoading(false);
    }
  }, [tierType, selectedTier]);

  useEffect(() => {
    loadConcernList();
  }, [loadConcernList]);

  //Predictors List Api Call
  const loadPredictorsList = useCallback(async () => {
    setPredictorLoading(true);
    setIsUpdatePredictor(false);
    try {
      const result = await postData("heading/list", {
        sortQuery: { created_at: 1 },
        status: 1,
        type: "predictors",
        isGlobal: true,
        tierType: tierType,
        ...(tierType === PositionTier && { tierId: selectedTier }),
        limit: 9999,
      });

      if (result.data) {
        let _predictors = [];
        let globalPredictors = [];
        result.data.map((item) => {
          globalPredictors.push({
            ...item,
            weight: (item?.company_headings && item?.company_headings.length > 0)
              ? item?.company_headings[0]?.weight
              : item?.weight || 0,
            isDefault: item?.company_headings.length > 0 ? true : false,
            disabled: false,
          });
          if (item?.company_headings.length > 0) {
            _predictors.push({
              _id: item?.company_headings[0]?._id,
              name: item?.company_headings[0]?.name,
              description: item?.company_headings[0]?.description,
              alias: item?.company_headings[0]?.alias,
              weight: item?.company_headings[0]?.weight || 0,
              created_at: new Date(),
              isDefault: item?.isDefault ? item?.isDefault : false,
              disabled: false,
              tierId: item?.tierId,
            });
          }
        });
        const getPredictorValue = result.data?.filter(
          (item) => item?.isDefault && item?.type === "predictors"
        );
        setWeightTotalValue(sumTotal(getPredictorValue, "weight"));
        setPredictors(globalPredictors);
        setSelectedPredictors(_predictors);
        setIsUpdatePredictor(true);
      }
      setPredictorLoading(false);
    } catch (err) {
      setPredictorLoading(false);
      console.log(err.message);
    } finally {
      setPredictorLoading(false);
    }
  }, [tierType, selectedTier]);

  useEffect(() => {
    loadPredictorsList();
  }, [loadPredictorsList]);

  // Handle weight change for predictors
  const handlePredictorWeightChange = (id, newWeight) => {
    setPredictors((prevPredictors) =>
      prevPredictors.map((item) => {
        if (item._id === id) {
          return {
            ...item,
            weight: parseFloat(newWeight) || 0,
          };
        }
        return item;
      })
    );
    setIsUpdatePredictor(true);
  };

  const updateHandler = (heading_id, event, type) => {
    if (event) {
      if (type === "concern") {
        let enabledConcerns =
          concerns.length > 0 && concerns.filter((it) => it?.isDefault)?.length;
        if (Number(enabledConcerns + 1) === 8) {
          setConcerns((concern) =>
            concern.map((item, i) => {
              if (item?._id === heading_id) {
                return {
                  ...item,
                  isDefault: true,
                };
              } else {
                if (item?.isDefault === false) {
                  return {
                    ...item,
                    disabled: true,
                  };
                } else {
                  return item;
                }
              }
            })
          );
        } else {
          setConcerns((concern) =>
            concern.map((item, i) => {
              if (item?._id === heading_id) {
                return {
                  ...item,
                  isDefault: true,
                };
              } else {
                return item;
              }
            })
          );
        }
        setIsUpdateConcern(true);
      } else if (type === "predictors") {
        let enabledPredictors =
          predictors.length > 0 &&
          predictors.filter((it) => it?.isDefault)?.length;
        if (Number(enabledPredictors + 1) === 8) {
          setPredictors((predictors) =>
            predictors.map((item, i) => {
              if (item?._id === heading_id) {
                return {
                  ...item,
                  isDefault: true,
                };
              } else {
                if (item?.isDefault === false) {
                  return {
                    ...item,
                    disabled: true,
                  };
                } else {
                  return item;
                }
              }
            })
          );
        } else {
          setPredictors((predictors) =>
            predictors.map((item, i) => {
              if (item?._id === heading_id) {
                return {
                  ...item,
                  isDefault: true,
                };
              } else {
                return item;
              }
            })
          );
        }
        setIsUpdatePredictor(true);
      }
    } else {
      if (type === "concern") {
        setConcerns((concern) =>
          concern.map((item, i) => {
            if (item?._id === heading_id) {
              return {
                ...item,
                isDefault: false,
              };
            } else {
              if (item?.isDefault === false) {
                return {
                  ...item,
                  disabled: false,
                };
              } else {
                return item;
              }
            }
          })
        );
        setIsUpdateConcern(true);
      } else if (type === "predictors") {
        setPredictors((predictors) =>
          predictors.map((item, i) => {
            if (item?._id === heading_id) {
              return {
                ...item,
                isDefault: false,
                weight: 0, // Reset weight when unchecking
              };
            } else {
              if (item?.isDefault === false) {
                return {
                  ...item,
                  disabled: false,
                };
              } else {
                return item;
              }
            }
          })
        );
        setIsUpdatePredictor(true);
      }
    }
  };

  useEffect(() => {
    let enabledConcerns =
      concerns.length > 0 && concerns.filter((it) => it?.isDefault)?.length;
    if (enabledConcerns > 0) {
      if (enabledConcerns === 8) {
        setConcerns((concern) =>
          concern.map((item, i) => {
            if (item?.isDefault === false) {
              return {
                ...item,
                disabled: true,
              };
            } else {
              return item;
            }
          })
        );
        setIsUpdateConcern(false);
      } else {
        setConcerns((concern) =>
          concern.map((item, i) => {
            if (item?.isDefault === false) {
              return {
                ...item,
                disabled: false,
              };
            } else {
              return item;
            }
          })
        );
        setIsUpdateConcern(false);
      }
    }
  }, [isUpdateConcern]);

  useEffect(() => {
    let enabledPredictors =
      predictors.length > 0 && predictors.filter((it) => it?.isDefault)?.length;
    if (enabledPredictors > 0) {
      if (enabledPredictors === 8) {
        setPredictors((predictors) =>
          predictors.map((item, i) => {
            if (item?.isDefault === false) {
              return {
                ...item,
                disabled: true,
              };
            } else {
              return item;
            }
          })
        );
        setIsUpdatePredictor(false);
      } else {
        setPredictors((predictors) =>
          predictors.map((item, i) => {
            if (item?.isDefault === false) {
              return {
                ...item,
                disabled: false,
              };
            } else {
              return item;
            }
          })
        );
        setIsUpdatePredictor(false);
      }
    }
  }, [isUpdatePredictor]);

  const onSubmit = async () => {
    // Get selected predictors
    const selectedPredictorsData = predictors.filter((item) => item?.isDefault);
    
    // Validate that at least one predictor is selected
    if (selectedPredictorsData.length === 0) {
      toastr.error("Please select at least one predictor.");
      return;
    }

    // Validate that total weight equals 100%
    if (weightTotalValue !== 100) {
      toastr.error(
        `The total weight of selected predictors must be exactly 100%. Current total: ${weightTotalValue.toFixed(1)}%`
      );
      return;
    }

    // Validate that all selected predictors have weight > 0
    const hasZeroWeight = selectedPredictorsData.some((item) => !item.weight || item.weight === 0);
    if (hasZeroWeight) {
      toastr.error("All selected predictors must have a weight greater than 0.");
      return;
    }

    try {
      setLoading(true);
      let concernsData;
      let predictorsData;
      
      concernsData =
        concerns &&
        Array?.isArray(concerns) &&
        concerns?.length > 0 &&
        concerns?.map((item) => ({
          ...item,
        }));
        
      predictorsData =
        predictors &&
        Array?.isArray(predictors) &&
        predictors?.length > 0 &&
        predictors?.map((item) => ({
          ...item,
          // Ensure weight is 0 for non-selected items
          weight: item?.isDefault ? (item?.weight || 0) : 0,
        }));
        
      const res = await postData("heading/checking-user-tire", {
        concerns: concernsData,
        predictors: predictorsData,
        tierType: tierType,
        ...(tierType === PositionTier && { tierId: selectedTier }),
      });
      
      if (res.status === 200) {
        setIsUpdateConcern(false);
        setIsUpdatePredictor(false);
        const result = await postData("heading/update-user-tire", {
          concerns: concernsData,
          predictors: predictorsData,
          tierType: tierType,
          ...(tierType === PositionTier && { tierId: selectedTier }),
        });
        if (result.status && result.status === 200) {
          toastr.success(result.message);
          setLoading(false);
          loadConcernList();
          loadPredictorsList();
          setIsUpdateConcern(true);
          setIsUpdatePredictor(true);
        } else {
          toastr.error(result.message);
          setLoading(false);
        }
      } else {
        if (res.type === 1) {
          toastr.error(res.message);
        } else if (res.type === 2) {
          setMessage(res.message);
          setEnabledConfirmationPopup(true);
        } else {
          toastr.error(res.message);
        }
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
    }
  };

  const onSuccess = () => {
    navigate("/concern?slug=step");    
  }

  const onUpdate = async () => {
    let concernsData;
    let predictorsData;
    concernsData =
      concerns &&
      Array?.isArray(concerns) &&
      concerns?.length > 0 &&
      concerns?.map((item) => ({
        ...item,
      }));
    predictorsData =
      predictors &&
      Array?.isArray(predictors) &&
      predictors?.length > 0 &&
      predictors?.map((item) => ({
        ...item,
        // Ensure weight is 0 for non-selected items
        weight: item?.isDefault ? (item?.weight || 0) : 0,
      }));
    try {
      setLoading(true);
      setIsUpdateConcern(false);
      setIsUpdatePredictor(false);
      const result = await postData("heading/update-user-tire", {
        concerns: concernsData,
        predictors: predictorsData,
        tierType: tierType,
        ...(tierType === PositionTier && { tierId: selectedTier }),
      });
      if (result.status && result.status === 200) {
        toastr.success(result.message);
        setEnabledConfirmationPopup(false);
        setLoading(false);
        loadConcernList();
        loadPredictorsList();
        setIsUpdateConcern(true);
        setIsUpdatePredictor(true);
      } else {
        toastr.error(result.message);
        setLoading(false);
      }
    } catch (error) {
      toastr.error(error.message);
      setLoading(false);
    }
  };

  // Calculate total weight whenever predictors change
  useEffect(() => {
    if (Array.isArray(predictors)) {
      const getPredictorValue = predictors.filter((item) => item?.isDefault);
      const total = sumTotal(getPredictorValue, "weight");
      setWeightTotalValue(total);
    }
  }, [predictors]);

  return (
    <>
      <div className="border bg-white border-gray-200 p-4 shadow-md rounded-xl">
        <div className="relative flex justify-between items-center mb-3 gap-4">
          <div className="text-2xl font-bold">{"Questions Library"}</div>
          <div className="flex space-x-1">
            {
              tierType !== PositionTier &&
              <div
                className="sm:ml-auto flex justify-end items-center"
                data-tooltip-id={`buttonExtend${1}`}
                data-tooltip-place="top"
              >             
                <Button
                  buttonType={"button"}
                  buttonIcon={"fa-regular fa-arrow-right-to-arc"}
                  buttonIconPosition={"left"}
                  buttonLabel={"Extended Tier"}
                  buttonHasLink={false}
                  buttonClasses={"!bg-slate-600"}
                  functions={() => 
                    setModalOpen({ open: true, data: {} })
                  }
                />
                <Tooltip
                  id={`buttonExtend${1}`}
                  className="!text-xs !bg-slate-500 z-50 rounded py-3 px-6 !opacity-100"
                  render={() => (
                    <div className="gap-x-1">
                      <div className="text-[13px] font-normal leading-none flex items-center">
                        <div className="font-semibold">Extended Tier</div>
                      </div>
                    </div>
                  )}
                />
              </div>
            }            
            <div
              className="sm:ml-auto flex justify-end items-center"
              data-tooltip-id={`buttonSettings${1}`}
              data-tooltip-place="top"
            >             
              <Button
                buttonType={"button"}
                buttonIcon={"fa-regular fa-gear"}
                buttonIconPosition={"left"}
                buttonLabel={"Setting Question Group"}
                buttonHasLink={false}
                functions={() => {
                  setIsOpen({ _id: null, open: true });
                }}
              />
              <Tooltip
                id={`buttonSettings${1}`}
                className="!text-xs !bg-teal-500 z-50 rounded py-3 px-6 !opacity-100"
                render={() => (
                  <div className="gap-x-1">
                    <div className="text-[13px] font-normal leading-none flex items-center">
                      <div className="font-semibold">Setting Question Group</div>
                    </div>
                  </div>
                )}
              />
            </div>
          </div>
          
        </div>
        {tierType === PositionTier ? (
          <>
            <PositionWideQuestionLibrary
              selectedConcern={selectedConcern}
              selectedPredictors={selectedPredictors}
              selectedTier={selectedTier}
              setSelectedTier={setSelectedTier}
              tierType={tierType}
              setEditData={setEditData}
              setEnabledPopup={setEnabledPopup}
            />
          </>
        ) : (
          <>
            <div className="relative mt-6 p-4 bg-white shadow-md rounded-lg border border-gray-200">
              <div className="text-xl font-semibold text-teal-500">
                Selected Questions
              </div>
              <div className="relative flex flex-col gap-4 mb-3">
                <div className="grid grid-cols-2 gap-4 p-2 overflow-auto scroll-smooth scrollbar">
                  <div className="relative">
                    {!concernLoading && (
                      <SelectedQuestionsItem
                        data={selectedConcern}
                        type="concern"
                        setEditData={setEditData}
                        setEnabledPopup={setEnabledPopup}
                      />
                    )}
                  </div>
                  <div className="relative">
                    {!predictorLoading && (
                      <SelectedQuestionsItem
                        data={selectedPredictors}
                        type="predictors"
                        setEditData={setEditData}
                        setEnabledPopup={setEnabledPopup}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
        <div className="relative mt-6 p-4 bg-white shadow-md rounded-lg border border-gray-200">
          <div className="text-xl font-semibold text-teal-500">
            Select Questions
          </div>
          {tierType === PositionTier && (
            <TierSelectDropdown
              selectedTier={selectedTier}
              setSelectedTier={setSelectedTier}
              isLable={false}
              isDefaultTier={true}
              isSelect={false}
            />
          )}
          <div className="bg-rose-100 border-l-4 border-rose-400 text-rose-700 p-4 rounded-lg flex items-start mt-4">
            <i className="fa-solid fa-triangle-exclamation text-rose-600 mr-3 mt-1"></i>
            <div>
              <p className="font-semibold">CAUTION!</p>
              <p>
                {` Your account is currently set up to use questions on a ${
                  tierType === PositionTier ? "Position Wide" : "Company Wide"
                } basis. If changes are made, they will affect all current succession plans.`}
              </p>
            </div>
          </div>

          <div className="relative mt-6 p-4 rounded-lg">
            <div className="relative flex flex-col gap-4 mb-3">
              <div className="grid grid-cols-2 gap-4 p-2 overflow-auto scroll-smooth scrollbar">
                <div className="relative p-2 rounded-lg border border-slate-400 shadow-sm">
                  <div className="text-xl font-semibold text-slate-500">
                    Concerns - ({concerns?.length})
                  </div>
                  <div className="sm:ml-auto flex justify-end items-center mt-2">
                    <div
                      data-tooltip-id={`buttonAddconcern${1}`}
                      data-tooltip-place="bottom"
                    >
                      <Button
                        buttonType={"button"}
                        buttonIcon={"fa-light fa-plus"}
                        buttonIconPosition={"left"}
                        buttonLabel={t("add")}
                        buttonHasLink={false}
                        functions={() => {
                          setEnabledPopup({ open: true, type: "concern" });
                          setEditData({});
                        }}
                      />
                      <Tooltip
                        id={`buttonAddconcern${1}`}
                        className="!text-xs !bg-teal-500 z-50 rounded py-3 px-6 !opacity-100"
                        render={() => (
                          <div className="gap-x-1">
                            <div className="text-[13px] font-normal leading-none flex items-center">
                              <div className="font-semibold">
                                Add New Concern
                              </div>
                            </div>
                          </div>
                        )}
                      />
                    </div>
                  </div>
                  <div className="relative">
                    {concernLoading && (
                      <div className="px-2 py-2">
                        <ManageDefaultConcernAndPredictorsSkleton
                          rowValue={"!grid-cols-1"}
                          count={10}
                        />
                      </div>
                    )}
                    {!concernLoading && (
                      <QuestionItems
                        data={concerns}
                        updateHandler={updateHandler}
                        type="concern"
                        setEditData={setEditData}
                        setEnabledPopup={setEnabledPopup}
                        loadConcernList={loadConcernList}
                        loadPredictorsList={loadPredictorsList}
                        onWeightChange={handlePredictorWeightChange}
                        totalWeight={weightTotalValue}
                      />
                    )}
                  </div>
                </div>
                <div className="relative p-2 rounded-lg border border-slate-400 shadow-sm">
                  <div className="text-xl font-semibold text-slate-500">
                    Predictors - ({predictors?.length})
                  </div>

                  <div className="flex justify-between items-center mt-2 w-full gap-2">
                    {/* Weight Display */}
                    <div
                      className={`text-xs sm:text-sm flex justify-center items-center px-4 h-8 sm:h-10 rounded transition-colors ${
                        weightTotalValue === 100
                          ? "bg-teal-500 text-white"
                          : weightTotalValue > 100
                          ? "bg-red-500 text-white"
                          : "bg-orange-500 text-white"
                      }`}
                    >
                      <i className={`fa-light ${
                        weightTotalValue === 100 
                          ? "fa-check-circle" 
                          : "fa-exclamation-triangle"
                      } mr-2`}></i>
                      {t("weight")}: {weightTotalValue.toFixed(1)}%
                      {weightTotalValue !== 100 && (
                        <span className="ml-2 text-xs">
                          ({weightTotalValue < 100 ? "Need" : "Over"} {Math.abs(100 - weightTotalValue).toFixed(1)}%)
                        </span>
                      )}
                    </div>

                    {/* Add Button */}
                    <div
                      data-tooltip-id={`buttonAddpredictors${1}`}
                      data-tooltip-place="bottom"
                    >
                      <Button
                        buttonType={"button"}
                        buttonIcon={"fa-light fa-plus"}
                        buttonIconPosition={"left"}
                        buttonLabel={t("add")}
                        buttonHasLink={false}
                        functions={() => {
                          setEnabledPopup({ open: true, type: "predictors" });
                          setEditData({});
                        }}
                      />
                      <Tooltip
                        id={`buttonAddpredictors${1}`}
                        className="!text-xs !bg-teal-500 z-50 rounded py-3 px-6 !opacity-100"
                        render={() => (
                          <div className="gap-x-1">
                            <div className="text-[13px] font-normal leading-none flex items-center">
                              <div className="font-semibold">Add New Predictors</div>
                            </div>
                          </div>
                        )}
                      />
                    </div>
                  </div>

                  <div className="relative">
                    {predictorLoading && (
                      <div className="px-2 py-2">
                        <ManageDefaultConcernAndPredictorsSkleton
                          rowValue={"!grid-cols-1"}
                          count={10}
                        />
                      </div>
                    )}
                    {!predictorLoading && (
                      <QuestionItems
                        data={predictors}
                        updateHandler={updateHandler}
                        type="predictors"
                        setEditData={setEditData}
                        setEnabledPopup={setEnabledPopup}
                        loadConcernList={loadConcernList}
                        loadPredictorsList={loadPredictorsList}
                        onWeightChange={handlePredictorWeightChange}
                        totalWeight={weightTotalValue}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-4 mt-5">
              <div className="flex items-end justify-end gap-4">
                <Button
                  buttonClasses=""
                  buttonLabelClasses=""
                  buttonType={"button"}
                  buttonIcon={
                    loading
                      ? "fa-light fa-spinner fa-spin"
                      : "fa-light fa-check"
                  }
                  buttonIconPosition={"left"}
                  buttonLabel={t("save")}
                  functions={() => {
                    onSubmit();
                  }}
                  buttonHasLink={false}
                  isDisabled={loading || weightTotalValue !== 100}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {enabledPopup?.open && (
        <HeadingManagePopup
          isOpen={enabledPopup?.open}
          setIsOpen={(val) => setEnabledPopup(val)}
          data={editData}
          setData={setEditData}
          getActionValue={(obj) => {
            setActionValue(obj);
          }}
          type={enabledPopup?.type}
          loadConcernList={loadConcernList}
          loadPredictorsList={loadPredictorsList}
        />
      )}
      {enabledConfirmationPopup && (
        <ConfirmationPopup
          isOpen={enabledConfirmationPopup}
          title={t("Warning")}
          message={
            message ||
            "If you modify the default questions, the rated successor values in the Succession Plan will be lost. If you still want to proceed, click the Confirm button."
          }
          confirmButtonLabel={t("Confirm")}
          cancelButtonLabel={t("cancel")}
          getActionValue={(obj) => {
            if (obj && obj.button_type === "confirm") {
              onUpdate();
            } else if (obj && obj.button_type === "close") {
              setEnabledConfirmationPopup(false);
            }
          }}
        />
      )}
      {modalOpen?.open && (
        <ExtendedTierPopup
          isOpen={modalOpen?.open}
          setIsOpen={setModalOpen}
          data={modalOpen?.data}
          loadList={onSuccess}
        />
      )}
    </>
  );
};

export default QuestionsLibrary;